/**
 * Best-effort numeric parser for Market Size inputs.
 *
 * Founders type free-form values like "5 million SMEs", "USD 2bn", "$500K",
 * "1.5B users", "~250,000-300,000". We convert those to a plain Number
 * (units expressed as a single scale — the units aren't compared across
 * TAM/SAM/SOM, we assume the founder is consistent). Returns null when the
 * string can't be parsed — the caller treats null as "skip validation".
 *
 * When the input is a range (e.g. "250,000–300,000"), we return the UPPER
 * bound so the sense-check leans permissive.
 */

// Word-based scale multipliers.
const SCALES = {
  k: 1e3, thousand: 1e3,
  m: 1e6, mn: 1e6, million: 1e6, mm: 1e6,
  b: 1e9, bn: 1e9, billion: 1e9,
  t: 1e12, trillion: 1e12,
};

const NUMBER_TOKEN = /(\d[\d,]*(?:\.\d+)?)\s*([kmbt]|mn|bn|mm|thousand|million|billion|trillion)?\b/gi;

/**
 * @param {string} raw
 * @returns {number | null}
 */
export function parseMarketValue(raw) {
  if (!raw || typeof raw !== "string") return null;
  const cleaned = raw.replace(/[~≈]/g, "").trim();
  if (!cleaned) return null;

  // Extract all number+optional-scale tokens; take the highest-magnitude one
  // so ranges like "250,000-300,000" resolve to 300000 (upper bound).
  const matches = [];
  let m;
  NUMBER_TOKEN.lastIndex = 0;
  while ((m = NUMBER_TOKEN.exec(cleaned)) !== null) {
    const numStr = m[1].replace(/,/g, "");
    const num = Number(numStr);
    if (!Number.isFinite(num)) continue;
    const scaleKey = (m[2] || "").toLowerCase();
    const scale = SCALES[scaleKey] || 1;
    matches.push(num * scale);
  }
  if (matches.length === 0) return null;
  return Math.max(...matches);
}

/**
 * Enforce SOM ≤ SAM ≤ TAM. Returns an array of issue objects — empty if OK
 * or if any value is unparseable (we don't nag on partial input).
 * Accepts either lowercase (legacy) or uppercase (iter54 MarketSizeForm)
 * keys — the iter54 form pivots parseMarketSize output which uses
 * uppercase ring labels as object keys.
 * @param {{tam?:string, sam?:string, som?:string, TAM?:string, SAM?:string, SOM?:string}} sizes
 * @returns {Array<{level:'warn'|'error', message:string}>}
 */
export function senseCheckMarketSizes(sizes) {
  const issues = [];
  const tam = parseMarketValue(sizes?.TAM ?? sizes?.tam);
  const sam = parseMarketValue(sizes?.SAM ?? sizes?.sam);
  const som = parseMarketValue(sizes?.SOM ?? sizes?.som);
  // Only nag when we can numerically compare — a founder mid-typing gets
  // silence, not a wall of red.
  if (tam !== null && sam !== null && sam > tam) {
    issues.push({
      level: "error",
      message: "SAM cannot be larger than TAM. Your Serviceable market shouldn't exceed your Total market.",
    });
  }
  if (sam !== null && som !== null && som > sam) {
    issues.push({
      level: "error",
      message: "SOM cannot be larger than SAM. Your Obtainable slice shouldn't exceed what you can Serve.",
    });
  }
  if (tam !== null && som !== null && som > tam) {
    issues.push({
      level: "error",
      message: "SOM cannot be larger than TAM.",
    });
  }
  return issues;
}
